import React from "react";
import "@/App.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import {
  WorkspaceProvider,
  useWorkspaces,
} from "@/contexts/WorkspaceContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import JoinPage from "@/pages/JoinPage";
import WorkspacePage from "@/pages/Workspace";
import AllChatsPage from "@/pages/AllChats";
import SuperadminPage from "@/pages/Superadmin";
import LegalTermsPage from "@/pages/LegalTerms";
import LegalPrivacyPage from "@/pages/LegalPrivacy";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import { Toaster } from "@/components/ui/sonner";

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#FAF9F7]">
        <p className="font-sans text-sm text-[#7A7571]">Loading…</p>
      </div>
    );
  }
  if (!user || !user.id) return <Navigate to="/login" replace />;
  return <WorkspaceProvider>{children}</WorkspaceProvider>;
}

/** A wrapper that always mounts WorkspaceProvider — even when the user is
 *  logged out. The provider's effect skips the API fetch when user.id is
 *  falsy, so this is safe AND it keeps the React element type stable across
 *  the user=null → user=set transition during signup. The earlier
 *  conditional version was unmounting/remounting the child tree (SignupPage)
 *  on signup, wiping out submittedRef + phase state and racing the user to
 *  RootRedirect → /all-chats before the create-ws phase could mount. */
function OptionalWorkspaceProvider({ children }) {
  return <WorkspaceProvider>{children}</WorkspaceProvider>;
}

function RootRedirect() {
  const { workspaces, loading } = useWorkspaces();
  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#FAF9F7]">
        <p className="font-sans text-sm text-[#7A7571]">
          Loading workspaces…
        </p>
      </div>
    );
  }
  if (workspaces.length === 0) return <Navigate to="/all-chats" replace />;
  return <Navigate to={`/w/${workspaces[0].slug}`} replace />;
}

/** `/` route: landing page if logged out, redirect to active workspace
 *  if logged in. We don't gate the landing page behind RequireAuth because
 *  we explicitly want public visitors to see it. */
function HomeOrLanding() {
  const { user } = useAuth();
  if (user === null) {
    return <LandingPage />; // LandingPage handles its own loading state
  }
  if (user && user.id) {
    return (
      <WorkspaceProvider>
        <RootRedirect />
      </WorkspaceProvider>
    );
  }
  return <LandingPage />;
}

function App() {
  return (
    <div className="App font-sans">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<HomeOrLanding />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/reset-password/:token"
              element={<ResetPasswordPage />}
            />
            <Route path="/legal/terms" element={<LegalTermsPage />} />
            <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
            <Route
              path="/signup"
              element={
                <OptionalWorkspaceProvider>
                  <SignupPage />
                </OptionalWorkspaceProvider>
              }
            />
            <Route
              path="/join/:token"
              element={
                <OptionalWorkspaceProvider>
                  <JoinPage />
                </OptionalWorkspaceProvider>
              }
            />

            {/* Superadmin — gated inside the page itself */}
            <Route path="/superadmin" element={<SuperadminPage />} />

            {/* Auth-gated */}
            <Route
              path="/all-chats"
              element={
                <RequireAuth>
                  <AllChatsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/w/:slug/c/:chatId"
              element={
                <RequireAuth>
                  <WorkspacePage />
                </RequireAuth>
              }
            />
            <Route
              path="/w/:slug"
              element={
                <RequireAuth>
                  <WorkspacePage />
                </RequireAuth>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              fontFamily: "IBM Plex Sans, ui-sans-serif, system-ui",
              borderRadius: "12px",
              border: "1px solid #DCD7CE",
            },
          }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
